import { useState } from 'react';
import { epostaDenetle, telefonDenetle, telefonYazarken, type IletisimSonucu } from '@artmozaik/shared';
import { api } from '../lib/api';
import Pencere from './Pencere';

/**
 * CARİ İLETİŞİM PENCERESİ — carinin cep telefonu ve e-postasını bulunduğunuz ekrandan çıkmadan
 * cari kartına yazar. Denetim ortak pakettedir (ekran = sunucu); hatalı değer kaydedilmez.
 * Yalnız DEĞİŞEN alan gönderilir. Kaydedince `kaydedildi` çağrılır; çağıran veriyi arka planda tazeler.
 */
type Alan = 'telefon' | 'eposta';
export type IletisimKaydi = { phone: string | null; email: string | null; degisen: Alan[] };

export default function IletisimPenceresi({ cariId, cariAd, telefon, eposta, odak, kapat, kaydedildi }: {
  cariId: number; cariAd: string; telefon: string | null; eposta: string | null;
  /** Açılışta imlecin duracağı alan. */
  odak: Alan; kapat: () => void; kaydedildi: (k: IletisimKaydi) => void;
}) {
  const ilk = { telefon: telefon ? telefonYazarken(telefon) : '', eposta: eposta ?? '' };
  const [deger, setDeger] = useState(ilk);
  const [dokundu, setDokundu] = useState<Record<Alan, boolean>>({ telefon: false, eposta: false });
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<{ alan: Alan | null; metin: string } | null>(null);

  const denet: Record<Alan, IletisimSonucu> = { telefon: telefonDenetle(deger.telefon), eposta: epostaDenetle(deger.eposta) };
  const degisti = (a: Alan) => deger[a].trim() !== ilk[a].trim();
  /** Boş bırakılan ve zaten boş olan alan "dokunulmamış"tır; dolu alan buradan silinemez. */
  const durum = (a: Alan): 'bos' | 'ayni' | 'gecerli' | 'hatali' =>
    !deger[a].trim() ? (ilk[a] ? 'hatali' : 'bos') : denet[a].ok ? (degisti(a) ? 'gecerli' : 'ayni') : 'hatali';
  const alanlar: Alan[] = ['telefon', 'eposta'];
  const gonderilecek = alanlar.filter((a) => durum(a) === 'gecerli');
  const engel = alanlar.some((a) => durum(a) === 'hatali');
  const hazir = gonderilecek.length > 0 && !engel;

  async function kaydet() {
    setDokundu({ telefon: true, eposta: true });
    if (!hazir || mesgul) return;
    setMesgul(true); setHata(null);
    try {
      const r = await api<{ phone: string | null; email: string | null }>(`/api/hatirlatma/cari/${cariId}/iletisim`, {
        method: 'PUT', body: JSON.stringify(Object.fromEntries(gonderilecek.map((a) => [a, deger[a]]))),
      });
      kaydedildi({ phone: r.phone, email: r.email, degisen: gonderilecek });
    } catch (e) {
      setHata({ alan: null, metin: e instanceof Error ? e.message : 'Kaydedilemedi. Bağlantınızı denetleyip yeniden deneyin.' });
      setMesgul(false);
    }
  }

  const alan = (a: Alan) => {
    const d = denet[a], s = durum(a);
    const goster = s === 'hatali' && (dokundu[a] || Boolean(ilk[a]));
    const oneri = !d.ok ? d.oneri : undefined;
    const id = `iletisim-${a}`;
    return (
      <div className="ccd-field ccd-giris" data-durum={goster ? 'hatali' : s === 'gecerli' ? 'gecerli' : undefined}>
        <label className="ccd-label" htmlFor={id}>{a === 'telefon' ? 'Cep telefonu' : 'E-posta'}</label>
        <div className="ccd-giris-kutu">
          <input id={id} className="ccd-input" autoFocus={odak === a} value={deger[a]} disabled={mesgul}
                 type={a === 'telefon' ? 'tel' : 'email'} inputMode={a === 'telefon' ? 'tel' : 'email'}
                 autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} maxLength={a === 'telefon' ? 24 : 254}
                 placeholder={a === 'telefon' ? '0532 123 45 67' : 'ad@firma.com'}
                 aria-invalid={goster} aria-describedby={`${id}-not`}
                 onBlur={() => setDokundu((x) => ({ ...x, [a]: true }))}
                 onChange={(e) => {
                   const ham = e.target.value;
                   // Maske yalnız imleç sondayken uygulanır; ortadan düzeltirken imleç zıplamasın.
                   const sonda = e.target.selectionStart === ham.length;
                   setDeger((x) => ({ ...x, [a]: a === 'telefon' ? (sonda ? telefonYazarken(ham) : ham) : ham.replace(/\s/g, '') }));
                   setHata(null);
                 }} />
          {s === 'gecerli' && <span className="ccd-giris-tik" aria-hidden="true">✓</span>}
        </div>
        <div id={`${id}-not`} className={goster ? 'ccd-field-error' : 'ccd-field-hint'} aria-live="polite">
          {goster ? (
            !deger[a].trim() ? 'Bu alan buradan boşaltılamaz; silmek için cari kartını kullanın.' : <>
              {!d.ok && d.neden}
              {oneri && <> <button type="button" className="ccd-borc-bag" onClick={() => { setDeger((x) => ({ ...x, [a]: oneri })); document.getElementById(id)?.focus(); }}>{oneri} olarak düzelt</button></>}
            </>
          ) : s === 'gecerli' && d.ok ? <>Karta <b>{d.goster}</b> olarak yazılacak.</>
            : s === 'ayni' ? 'Kartta kayıtlı.'
            : a === 'telefon' ? 'Türkiye cep numarası. Yurt dışı için ülke kodunu + ile yazın: +49 151…' : 'Tek adres; boşluk ve Türkçe karakter olmadan.'}
        </div>
      </div>
    );
  };

  return (
    <Pencere baslik="İletişim bilgisi" alt={cariAd} kapat={kapat} gonder={() => void kaydet()} mesgul={mesgul}
      eylem={<>
        <button type="button" className="ccd-btn ccd-btn-ghost" onClick={kapat} disabled={mesgul}>Vazgeç</button>
        <button type="submit" className="ccd-btn ccd-btn-primary" disabled={mesgul || !hazir} data-fihrist="cari-iletisim-duzelt">{mesgul ? 'Kaydediliyor…' : 'Kaydet'}</button>
      </>}>
      {hata && <div className="ccd-field-error" role="alert">{hata.metin}</div>}
      {alan('telefon')}
      {alan('eposta')}
      <div className="ccd-meta">Bilgi doğrudan cari kartına yazılır; değişiklik Kayıt İzi'nde görünür.</div>
    </Pencere>
  );
}
